import React, { useEffect, useState, useCallback } from "react";
import { Modal, Button, Card } from "react-bootstrap";
import { FaStar } from "react-icons/fa";
import Cropper from 'react-easy-crop';
import { useAppDispatch, useAppSelector } from "../../hooks";
import { studentActions, attendanceActions, attendanceStudentRecordActions, classActions } from "../../redux/actions";
import { studentService } from "../../services/student.service";
import Loading from "../../common/Loading/loading";

interface StudentInformationModalProps {
  show: boolean;
  onClose: () => void;
  studentId: number;
}

interface AttendanceSummary {
  month: number;
  year: number;
  totalPresent: number;
  totalAbsent: number;
  daysPresent: number[];
  daysAbsent: number[];
  classDaysPresentMap: Record<string, number[]>;
  classDaysAbsentMap: Record<string, number[]>;
}

const StudentInformationModal: React.FC<StudentInformationModalProps> = ({ show, onClose, studentId }) => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [giftValue, setGiftValue] = useState<number>(0);
  const [isGiftModified, setIsGiftModified] = useState(false);
  const [isSavingGift, setIsSavingGift] = useState(false);
  const [attendanceSummary, setAttendanceSummary] = useState<AttendanceSummary[]>([]);
  const [localStudent, setLocalStudent] = useState<any>(null); // fallback for archived students

  // Image / crop states
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [croppedImage, setCroppedImage] = useState<string | null>(null);
  const [isSavingAvatar, setIsSavingAvatar] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  // Date range for attendance search
  const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  const [fromMonth, setFromMonth] = useState<string>(() => { const d = new Date(); d.setMonth(d.getMonth() - 3); return fmt(d); });
  const [toMonth,   setToMonth]   = useState<string>(() => fmt(new Date()));
  const [selectedMonths, setSelectedMonths] = useState<string[]>(() => {
    const result: string[] = [];
    for (let i = 3; i >= 0; i--) { const d = new Date(); d.setMonth(d.getMonth() - i); result.push(fmt(d)); }
    return result;
  });
  const [isSearchingAttendance, setIsSearchingAttendance] = useState(false);

  // Get data from Redux store
  const students = useAppSelector((state: any) => state.students.studentsList);
  const classes = useAppSelector((state: any) => state.classes.classesList);
  const attendanceSessions = useAppSelector((state: any) => state.attendanceSessions.attendanceSessionsList);
  const attendanceStudentRecords = useAppSelector((state: any) => state.attendanceStudentRecords.attendanceStudentRecordsList);

  // Find the specific student from the list (active or archived fallback)
  const reduxStudent = students?.find((s: any) => s.id === studentId);
  const student = reduxStudent ?? localStudent;

  useEffect(() => {
    if (!show || !studentId) return;

    setIsLoading(true);
    setLocalStudent(null);

    const currentDate = new Date();
    const months: string[] = [];

    // Get last 4 months
    for (let i = 0; i < 4; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      months.push(`${year}-${month}`);
    }

    // Only fetch attendance data - student and classes data already exist in store
    Promise.all([
      dispatch(attendanceActions.getAllAttendanceSessions()),
      dispatch(attendanceStudentRecordActions.getAttendanceStudentRecordByMonthsAndStudent(months, studentId)),
    ])
      .then(() => {
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching attendance data:", err);
        setIsLoading(false);
      });

    // If not found in Redux (archived student), fetch directly from BE
    if (!students?.find((s: any) => s.id === studentId)) {
      studentService.getById(studentId).then((res: any) => {
        const data = res?.data ?? res;
        if (data?.id) setLocalStudent(data);
      }).catch(() => {});
    }
  }, [show, studentId, dispatch]);

  // Initialize gift value when student data is loaded
  useEffect(() => {
    if (student) {
      setGiftValue(student.gift || 0);
      setIsGiftModified(false);
    }
  }, [student]);

  // Calculate attendance summary from selectedMonths
  useEffect(() => {
    if (!student || !attendanceSessions?.length || !attendanceStudentRecords?.length || !classes?.length) {
      setAttendanceSummary([]);
      return;
    }

    const monthsToShow = selectedMonths.map(ym => {
      const [y, m] = ym.split('-').map(Number);
      return { month: m, year: y };
    });

    const summaries: AttendanceSummary[] = monthsToShow.map(({ month, year }) => {
      const monthSessions = attendanceSessions.filter((s: any) => {
        const d = new Date(s.date);
        return d.getMonth() + 1 === month && d.getFullYear() === year;
      });
      const monthSessionIds = monthSessions.map((s: any) => s.id);

      const studentRecords = attendanceStudentRecords.filter(
        (r: any) => r.studentId === studentId && monthSessionIds.includes(r.sessionId)
      );

      const classDaysPresentMap: Record<string, number[]> = {};
      const classDaysAbsentMap: Record<string, number[]> = {};

      studentRecords.forEach((r: any) => {
        const session = attendanceSessions.find((s: any) => s.id === r.sessionId);
        if (!session) return;
        const className = classes.find((c: any) => c.id === session.classId)?.name || 'Không rõ lớp';
        const day = new Date(session.date).getDate();
        if (r.isPresent) {
          if (!classDaysPresentMap[className]) classDaysPresentMap[className] = [];
          classDaysPresentMap[className].push(day);
        } else {
          if (!classDaysAbsentMap[className]) classDaysAbsentMap[className] = [];
          classDaysAbsentMap[className].push(day);
        }
      });

      const daysPresent = Object.values(classDaysPresentMap).flat().sort((a, b) => a - b);
      const daysAbsent  = Object.values(classDaysAbsentMap).flat().sort((a, b) => a - b);

      return { month, year, totalPresent: daysPresent.length, totalAbsent: daysAbsent.length,
        daysPresent, daysAbsent, classDaysPresentMap, classDaysAbsentMap };
    });

    setAttendanceSummary(summaries.reverse()); // newest first
  }, [student, attendanceSessions, attendanceStudentRecords, classes, studentId, selectedMonths]);

  const handleGiftChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(e.target.value) || 0;
    setGiftValue(newValue);
    setIsGiftModified(newValue !== (student?.gift || 0));
  };

  const handleSaveGift = async () => {
    if (!student?.id || isSavingGift) return;

    setIsSavingGift(true);
    try {
      // Use Redux action instead of direct fetch to handle token properly
      await dispatch(studentActions.updateStudent({
        id: student.id,
        name: student.name,
        dateOfBirth: student.dateOfBirth,
        phoneNumber: student.phoneNumber,
        fbLink: student.fbLink,
        note: student.note,
        classId: student.classId,
        startDate: student.startDate,
        gift: giftValue, // Update gift value
      }));

      // Reload all students to update the list
      await dispatch(studentActions.getAll());
      setIsGiftModified(false);
    } catch (error: any) {
      console.error('Error saving gift:', error);
    } finally {
      setIsSavingGift(false);
    }
  };

  // ─── Attendance date-range helpers ───────────────────────────────────────────
  const generateMonthsRange = (from: string, to: string): string[] => {
    const [fy, fm] = from.split('-').map(Number);
    const [ty, tm] = to.split('-').map(Number);
    const months: string[] = [];
    let y = fy, m = fm;
    while (y < ty || (y === ty && m <= tm)) {
      months.push(`${y}-${String(m).padStart(2, '0')}`);
      m++; if (m > 12) { m = 1; y++; }
      if (months.length > 36) break; // safety cap 3 years
    }
    return months;
  };

  const handleSearchAttendance = async () => {
    if (!studentId || fromMonth > toMonth) return;
    const months = generateMonthsRange(fromMonth, toMonth);
    if (!months.length) return;
    setIsSearchingAttendance(true);
    setSelectedMonths(months);
    try {
      await dispatch(attendanceStudentRecordActions.getAttendanceStudentRecordByMonthsAndStudent(months, studentId));
    } finally {
      setIsSearchingAttendance(false);
    }
  };

  // Shared: read a File and open the cropper
  const handleFilePick = (file: File) => {
    if (!file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      setImageSrc(reader.result as string);
      setShowCropper(true);
      setCroppedImage(null);
    });
    reader.readAsDataURL(file);
  };

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    handleFilePick(file);
    // reset input so same file can be re-selected
    e.target.value = '';
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFilePick(file);
  };

  const onCropComplete = useCallback((_: any, croppedPixels: any) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const getCroppedImg = useCallback(async (imageSrcLocal: string, pixelCrop: any) => {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.setAttribute('crossOrigin', 'anonymous');
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
      img.src = imageSrcLocal;
    });

    const canvas = document.createElement('canvas');
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('No 2d context');

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return canvas.toDataURL('image/jpeg');
  }, []);

  const applyCrop = useCallback(async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    try {
      const cropped = await getCroppedImg(imageSrc, croppedAreaPixels);
      setCroppedImage(cropped);
      setShowCropper(false);
    } catch (err) {
      console.error('Crop error', err);
    }
  }, [imageSrc, croppedAreaPixels, getCroppedImg]);

  const clearImage = () => {
    setImageSrc(null);
    setCroppedImage(null);
    setShowCropper(false);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const handleSaveAvatar = async () => {
    if (!student?.id || !croppedImage || isSavingAvatar) return;

    setIsSavingAvatar(true);
    try {
      await dispatch(studentActions.updateStudent({
        id: student.id,
        name: student.name,
        dateOfBirth: student.dateOfBirth,
        phoneNumber: student.phoneNumber,
        fbLink: student.fbLink,
        note: student.note,
        classId: student.classId,
        startDate: student.startDate,
        gift: student.gift,
        avatar: croppedImage, // Update avatar
      }));

      // Reload all students to update the list
      await dispatch(studentActions.getAll());
      
      // Clear image states
      clearImage();
    } catch (error: any) {
      console.error('Error saving avatar:', error);
    } finally {
      setIsSavingAvatar(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="xl"
      centered
      style={{
        animation: 'fadeIn 0.3s ease-in-out',
      }}
    >
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: scale(0.95); }
            to { opacity: 1; transform: scale(1); }
          }
          
          .gradient-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 24px;
            border-radius: 8px 8px 0 0;
          }
          
          .profile-card {
            background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
            border-radius: 16px;
            padding: 32px;
            text-align: center;
            box-shadow: 0 10px 30px rgba(0,0,0,0.1);
            transition: transform 0.3s ease;
          }
          
          .profile-card:hover {
            transform: translateY(-5px);
          }
          
          .attendance-card {
            background: white;
            border-radius: 16px;
            padding: 24px;
            box-shadow: 0 10px 30px rgba(0,0,0,0.1);
            border-left: 4px solid #667eea;
          }
          
          .avatar-img {
            border: 4px solid white;
            box-shadow: 0 8px 20px rgba(0,0,0,0.15);
            transition: transform 0.3s ease;
          }
          
          .avatar-img:hover {
            transform: scale(1.05);
          }
        `}
      </style>

      <div className="gradient-header">
        <h4 className="mb-0">📚 Thông tin học sinh</h4>
      </div>

      <Modal.Body style={{ padding: '24px 32px', background: '#f8f9fa' }}>
        {isLoading ? (
          <Loading message="Đang tải thông tin học sinh..." />
        ) : (
          <div className="row">
            {/* Profile Card */}
            <div className="col-md-5">
              <div className="profile-card">
                <img
                  src={
                    student?.avatar
                      ? `${process.env.REACT_APP_API_URL}${student.avatar}`
                      : "https://bootdey.com/img/Content/avatar/avatar7.png"
                  }
                  alt="Profile"
                  className="rounded-circle mb-3 avatar-img"
                  style={{ width: "140px", height: "140px", objectFit: "cover" }}
                />
                
                {/* Upload avatar section */}
                <div style={{ marginBottom: '20px' }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={onFileChange}
                    id="avatar-upload"
                    style={{ display: 'none' }}
                  />

                  {/* Drag-and-drop zone */}
                  <div
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop}
                    style={{
                      border: `2px dashed ${isDragOver ? '#764ba2' : '#a78bfa'}`,
                      borderRadius: 14,
                      padding: '16px 12px',
                      background: isDragOver
                        ? 'linear-gradient(135deg,#ede9fe,#ddd6fe)'
                        : 'linear-gradient(135deg,#f5f3ff,#ede9fe)',
                      transition: 'all 0.2s',
                      cursor: 'pointer',
                      marginBottom: 12,
                    }}
                    onClick={() => document.getElementById('avatar-upload')?.click()}
                  >
                    <div style={{ fontSize: 28, marginBottom: 4 }}>
                      {isDragOver ? '📂' : '📷'}
                    </div>
                    <div style={{ fontWeight: 700, color: '#5b21b6', fontSize: 14, marginBottom: 2 }}>
                      {isDragOver ? 'Thả ảnh vào đây!' : 'Kéo thả hoặc bấm để chọn ảnh'}
                    </div>
                    <div style={{ fontSize: 11, color: '#7c3aed', opacity: 0.75 }}>
                      Hỗ trợ JPG, PNG, GIF, WEBP
                    </div>
                  </div>

                  {/* Cropper Modal */}
                  {showCropper && imageSrc && (
                    <div style={{
                      position: 'fixed',
                      top: 0, left: 0, right: 0, bottom: 0,
                      background: 'rgba(0,0,0,0.8)',
                      zIndex: 9999,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <div style={{
                        background: 'white',
                        borderRadius: '16px',
                        padding: '24px',
                        maxWidth: '600px',
                        width: '90%',
                      }}>
                        <h5 style={{ marginBottom: '16px' }}>Chỉnh sửa ảnh</h5>
                        <div style={{ position: 'relative', width: '100%', height: 300, background: '#333', borderRadius: '8px', overflow: 'hidden' }}>
                          <Cropper
                            image={imageSrc}
                            crop={crop}
                            zoom={zoom}
                            aspect={1}
                            onCropChange={setCrop}
                            onCropComplete={onCropComplete}
                            onZoomChange={setZoom}
                          />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                            <span style={{ fontSize: '14px', fontWeight: 'bold' }}>Zoom</span>
                            <input
                              type="range"
                              min={1}
                              max={3}
                              step={0.1}
                              value={zoom}
                              onChange={(e) => setZoom(Number(e.target.value))}
                              style={{ flex: 1 }}
                            />
                          </label>
                        </div>
                        <div style={{ display: 'flex', gap: 12, marginTop: 16, justifyContent: 'flex-end' }}>
                          <Button variant="secondary" onClick={clearImage}>Huỷ</Button>
                          <Button variant="primary" onClick={applyCrop}>Áp dụng</Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Preview cropped image and save button */}
                  {croppedImage && (
                    <div style={{ marginTop: '16px' }}>
                      <div style={{ marginBottom: 8, fontSize: 12, color: '#666', fontWeight: 'bold' }}>Ảnh đã chọn:</div>
                      <img
                        src={croppedImage}
                        alt="avatar preview"
                        style={{
                          width: 100, height: 100,
                          objectFit: 'cover',
                          borderRadius: '50%',
                          border: '3px solid #667eea',
                          marginBottom: '12px',
                        }}
                      />
                      <div>
                        <Button
                          variant="success"
                          onClick={handleSaveAvatar}
                          disabled={isSavingAvatar}
                          style={{
                            borderRadius: '20px',
                            padding: '8px 24px',
                            fontWeight: 'bold',
                            boxShadow: '0 4px 12px rgba(40, 167, 69, 0.3)',
                            opacity: isSavingAvatar ? 0.7 : 1,
                            minWidth: '120px',
                          }}
                        >
                          {isSavingAvatar ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Đang lưu...
                            </>
                          ) : (
                            '💾 Lưu ảnh'
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                <h5 className="mb-0" style={{ fontSize: '24px', fontWeight: 'bold', color: '#333' }}>
                  {student?.name || "Unknown"}
                </h5>

                {/* Gift stars display */}
                <div className="d-flex align-items-center justify-content-center mb-3 mt-4" style={{ gap: "12px" }}>
                  <input
                    type="number"
                    min="0"
                    value={giftValue}
                    onChange={handleGiftChange}
                    style={{
                      width: "90px",
                      padding: "8px 12px",
                      border: "2px solid #667eea",
                      borderRadius: "8px",
                      textAlign: "center",
                      fontSize: "20px",
                      fontWeight: "bold",
                      background: "white",
                    }}
                  />
                  <FaStar color="#ffc107" size={36} />
                </div>

                {/* Conditional Save button with loading */}
                {isGiftModified && (
                  <Button 
                    variant="success" 
                    onClick={handleSaveGift}
                    disabled={isSavingGift}
                    style={{
                      borderRadius: '20px',
                      padding: '8px 32px',
                      fontWeight: 'bold',
                      boxShadow: '0 4px 12px rgba(40, 167, 69, 0.3)',
                      opacity: isSavingGift ? 0.7 : 1,
                      minWidth: '100px',
                    }}
                  >
                    {isSavingGift ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Đang lưu...
                      </>
                    ) : (
                      'Lưu'
                    )}
                  </Button>
                )}
              </div>
            </div>

            {/* Attendance Summary Card */}
            <div className="col-md-7">
              <div className="attendance-card">

                {/* Date range picker */}
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontWeight: 700, color: '#667eea', fontSize: 15, marginBottom: 10 }}>📊 Thống kê điểm danh</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, color: '#555', fontWeight: 600, whiteSpace: 'nowrap' }}>Từ tháng</span>
                    <input
                      type="month"
                      value={fromMonth}
                      onChange={e => setFromMonth(e.target.value)}
                      style={{ border: '2px solid #667eea', borderRadius: 8, padding: '4px 8px', fontSize: 13, color: '#333', outline: 'none' }}
                    />
                    <span style={{ fontSize: 13, color: '#555', fontWeight: 600, whiteSpace: 'nowrap' }}>— đến tháng</span>
                    <input
                      type="month"
                      value={toMonth}
                      max={fmt(new Date())}
                      onChange={e => setToMonth(e.target.value)}
                      style={{ border: '2px solid #667eea', borderRadius: 8, padding: '4px 8px', fontSize: 13, color: '#333', outline: 'none' }}
                    />
                    <button
                      onClick={handleSearchAttendance}
                      disabled={isSearchingAttendance || fromMonth > toMonth}
                      style={{
                        padding: '5px 16px', borderRadius: 20, border: 'none',
                        background: 'linear-gradient(135deg,#667eea,#764ba2)',
                        color: '#fff', fontWeight: 700, fontSize: 13,
                        cursor: fromMonth > toMonth ? 'not-allowed' : 'pointer',
                        opacity: fromMonth > toMonth ? 0.6 : 1,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {isSearchingAttendance ? '⏳...' : '🔍 Tìm kiếm'}
                    </button>
                  </div>
                  {fromMonth > toMonth && (
                    <div style={{ fontSize: 11, color: '#dc3545', marginTop: 4 }}>Tháng bắt đầu phải nhỏ hơn hoặc bằng tháng kết thúc</div>
                  )}
                </div>

                {/* Scrollable results */}
                <div style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 420px)', paddingRight: 4 }}>
                  {attendanceSummary.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#94a3b8', padding: '40px 0', fontSize: 14 }}>
                      Không có dữ liệu điểm danh trong khoảng thời gian này.
                    </div>
                  ) : (
                    attendanceSummary.map((s) => (
                      <div key={`${s.month}-${s.year}`} className="mb-4">
                        <div style={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white', padding: '10px 14px',
                          borderRadius: '8px', marginBottom: '10px', fontWeight: 'bold', fontSize: 14,
                        }}>
                          Tháng {s.month}/{s.year} — {student?.name}
                        </div>

                        <div style={{ marginBottom: '8px' }}>
                          <strong style={{ color: '#28a745' }}>✓ Đi học:</strong> {s.totalPresent} buổi{' '}
                          {s.daysPresent.length > 0 && <>— Ngày: {s.daysPresent.join(', ')}</>}
                          {Object.keys(s.classDaysPresentMap || {}).length > 1 && (
                            <ul style={{ marginTop: '4px', paddingLeft: '20px' }}>
                              {Object.entries(s.classDaysPresentMap || {}).map(([className, days]) => {
                                const dayList = days as number[];
                                return (
                                  <li key={className}>
                                    <span style={{ fontWeight: 500 }}>{className}:</span>{' '}
                                    Ngày {dayList.sort((a, b) => a - b).join(', ')}
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </div>

                        <div style={{ color: '#dc3545' }}>
                          <strong>✗ Nghỉ học:</strong> {s.totalAbsent} buổi{' '}
                          {s.daysAbsent.length > 0 && <>— Ngày: {s.daysAbsent.join(', ')}</>}
                          {Object.keys(s.classDaysAbsentMap || {}).length > 1 && (
                            <ul style={{ marginTop: '4px', paddingLeft: '20px' }}>
                              {Object.entries(s.classDaysAbsentMap || {}).map(([className, days]) => (
                                <li key={className}>
                                  <span style={{ fontWeight: 500 }}>{className}:</span>{' '}
                                  Ngày {days.sort((a, b) => a - b).join(', ')}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>

                        {s !== attendanceSummary[attendanceSummary.length - 1] && (
                          <hr style={{ borderTop: '2px dashed #e0e0e0' }} />
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer style={{ borderTop: '2px solid #667eea', padding: '16px 32px' }}>
        <Button 
          variant="secondary" 
          onClick={onClose}
          style={{
            borderRadius: '20px',
            padding: '8px 24px',
            fontWeight: 'bold',
          }}
        >
          Đóng
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default StudentInformationModal;
